import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { CheckCircle2, Lock, AlertCircle } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  }

  if (sent) return (
    <div className="max-w-md mx-auto rounded-3xl bg-white card-soft p-8 mt-16 text-center dark:bg-zinc-900">
      <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
      <h1 className="font-display text-2xl mt-3" data-testid="forgot-success">Controlla la tua email</h1>
      <p className="text-sm text-zinc-600 mt-2 dark:text-zinc-400">Se l'indirizzo è registrato, riceverai un link per reimpostare la password entro pochi minuti. Il link scade tra 30 minuti.</p>
      <Link to="/login" className="btn-secondary inline-block mt-6">Torna al login</Link>
    </div>
  );

  return (
    <div className="max-w-md mx-auto rounded-3xl bg-white card-soft p-8 mt-16 dark:bg-zinc-900">
      <Lock className="h-10 w-10 text-blue-600 mx-auto" />
      <h1 className="font-display text-2xl mt-2 text-center">Password dimenticata?</h1>
      <p className="text-sm text-zinc-500 mt-2 text-center dark:text-zinc-400">Inserisci la tua email e riceverai un link per reimpostarla.</p>
      <form onSubmit={submit} className="mt-6 space-y-3">
        <label className="block">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Email</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-blue-500 focus:outline-none dark:border-zinc-600"
                 data-testid="forgot-email" />
        </label>
        {err && <div className="text-sm text-rose-600" data-testid="forgot-error">{err}</div>}
        <button type="submit" disabled={busy} className="btn-accent w-full disabled:opacity-50" data-testid="forgot-submit">
          {busy ? "Invio…" : "Invia link reset"}
        </button>
      </form>
      <Link to="/login" className="block text-sm text-zinc-500 text-center mt-4 hover:text-zinc-800 dark:text-zinc-400">Torna al login</Link>
    </div>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("La password deve avere almeno 8 caratteri"); return; }
    if (pw !== pw2) { setErr("Le password non coincidono"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Link non valido o scaduto");
    } finally {
      setBusy(false);
    }
  }

  if (!token) return (
    <div className="max-w-md mx-auto rounded-3xl bg-white card-soft p-8 mt-16 text-center dark:bg-zinc-900">
      <AlertCircle className="h-14 w-14 text-rose-500 mx-auto" />
      <h1 className="font-display text-2xl mt-3">Link non valido</h1>
      <p className="text-sm text-zinc-600 mt-2 dark:text-zinc-400">Manca il token di reset.</p>
      <Link to="/login" className="btn-secondary inline-block mt-6">Login</Link>
    </div>
  );

  if (done) return (
    <div className="max-w-md mx-auto rounded-3xl bg-white card-soft p-8 mt-16 text-center dark:bg-zinc-900">
      <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
      <h1 className="font-display text-2xl mt-3" data-testid="reset-success">Password aggiornata</h1>
      <p className="text-sm text-zinc-600 mt-2 dark:text-zinc-400">Verrai reindirizzato al login…</p>
    </div>
  );

  return (
    <div className="max-w-md mx-auto rounded-3xl bg-white card-soft p-8 mt-16 dark:bg-zinc-900">
      <Lock className="h-10 w-10 text-blue-600 mx-auto" />
      <h1 className="font-display text-2xl mt-2 text-center">Nuova password</h1>
      <form onSubmit={submit} className="mt-6 space-y-3">
        <label className="block">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nuova password</span>
          <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} minLength={8}
                 className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-blue-500 focus:outline-none dark:border-zinc-600"
                 data-testid="reset-pw" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Conferma password</span>
          <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} minLength={8}
                 className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-blue-500 focus:outline-none dark:border-zinc-600"
                 data-testid="reset-pw2" />
        </label>
        {err && <div className="text-sm text-rose-600" data-testid="reset-error">{err}</div>}
        <button type="submit" disabled={busy} className="btn-accent w-full disabled:opacity-50" data-testid="reset-submit">
          {busy ? "Aggiorno…" : "Imposta password"}
        </button>
      </form>
    </div>
  );
}
